import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { api } from "@/lib/api";

const CartContext = createContext(null);
const STORAGE_KEY = "nexus_cart_v2"; // bumped after introducing dose variants

function loadInitial() {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (raw) return JSON.parse(raw);
    } catch (err) {
        console.error("[Cart] failed to parse stored cart:", err);
    }
    return [];
}

function resolveVariant(product, dose) {
    if (!dose && product.pricing) {
        const entries = Object.entries(product.pricing);
        if (entries.length) {
            entries.sort((a, b) => a[1] - b[1]);
            return { dose: entries[0][0], price: entries[0][1] };
        }
    }
    if (dose && product.pricing && product.pricing[dose] != null) {
        return { dose, price: product.pricing[dose] };
    }
    return { dose: dose || null, price: product.price };
}

function variantKey(product_id, dose) {
    return `${product_id}::${dose || "default"}`;
}

export function CartProvider({ children }) {
    // Lazy initializer hydrates synchronously from localStorage — no useEffect needed.
    const [items, setItems] = useState(loadInitial);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        try {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
        } catch (err) {
            console.error("[Cart] failed to persist cart:", err);
        }
    }, [items]);

    // Abandoned-cart tracking: debounced sync to backend (logged-in users via
    // cookie; guests via the email they entered at checkout, kept in localStorage).
    useEffect(() => {
        const t = setTimeout(() => {
            const email = localStorage.getItem("nexus_checkout_email") || null;
            api.post("/cart/track", {
                email,
                items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity, dose: i.dose || null })),
            }).catch(() => {});
        }, 1500);
        return () => clearTimeout(t);
    }, [items]);

    const addItem = useCallback((product, quantity = 1, dose = null) => {
        const variant = resolveVariant(product, dose);
        const key = variantKey(product.id, variant.dose);
        setItems((prev) => {
            const i = prev.findIndex((x) => x.variant_id === key);
            if (i >= 0) {
                const next = [...prev];
                next[i] = { ...next[i], quantity: next[i].quantity + quantity };
                return next;
            }
            return [
                ...prev,
                {
                    variant_id: key,
                    product_id: product.id,
                    slug: product.slug,
                    name: product.name,
                    dose: variant.dose,
                    price: variant.price,
                    image: product.image,
                    quantity,
                },
            ];
        });
        setOpen(true);
    }, []);

    const updateQty = useCallback((variant_id, quantity) => {
        setItems((prev) =>
            prev
                .map((x) => (x.variant_id === variant_id ? { ...x, quantity: Math.max(0, quantity) } : x))
                .filter((x) => x.quantity > 0)
        );
    }, []);

    const removeItem = useCallback((variant_id) => {
        setItems((prev) => prev.filter((x) => x.variant_id !== variant_id));
    }, []);

    const clear = useCallback(() => setItems([]), []);

    // Bundle discount tiers — must mirror backend server.py::BUNDLE_TIERS
    const bundlePctFor = useCallback((qty) => {
        if (qty >= 5) return 0.10;
        if (qty >= 3) return 0.05;
        return 0;
    }, []);

    const { subtotal, count, bundleSavings, grossSubtotal } = useMemo(() => {
        let net = 0;
        let gross = 0;
        let savings = 0;
        let c = 0;
        for (const x of items) {
            const g = x.price * x.quantity;
            const pct = (x.quantity >= 5 ? 0.10 : x.quantity >= 3 ? 0.05 : 0);
            const disc = g * pct;
            gross += g;
            net += g - disc;
            savings += disc;
            c += x.quantity;
        }
        return {
            subtotal: Math.round(net * 100) / 100,
            grossSubtotal: Math.round(gross * 100) / 100,
            bundleSavings: Math.round(savings * 100) / 100,
            count: c,
        };
    }, [items]);

    const value = useMemo(
        () => ({
            items,
            open,
            setOpen,
            addItem,
            updateQty,
            removeItem,
            clear,
            subtotal,
            grossSubtotal,
            bundleSavings,
            bundlePctFor,
            count,
        }),
        [items, open, addItem, updateQty, removeItem, clear, subtotal, grossSubtotal, bundleSavings, bundlePctFor, count]
    );

    return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export const useCart = () => useContext(CartContext);
